import { AdminCohorts } from '@platform/client';
import {
  DatePickerWithTimezone,
  FormsCheckBox,
  FormsInputDropdown,
  InlineFormInput,
  InputDropdownItem,
} from '@platform/components';
import { Control, Controller, FormState, UseFormRegister } from 'react-hook-form';

export interface CohortFormData {
  name: string | null;
  clientId: number | null;
  available: boolean;
  engagementStartDate: string | null;
  engagementEndDate: string | null;
  wrapupDate: string | null;
  kickoffDate: string | null;
  applicationStartDate: string | null;
  applicationEndDate: string | null;
  duration: string | null;
  timeCommitment: string | null;
  templateId: string | null;
  url?: string | null;
  description?: string | null;
}

type CohortFormProps = {
  control: Control<CohortFormData>;
  formState: FormState<CohortFormData>;
  register: UseFormRegister<CohortFormData>;
};

export const CohortForm = (props: CohortFormProps) => {
  const { control, formState, register } = props;
  const { data: clientData } = AdminCohorts.useGetCohortClients();
  const { data: templateData } = AdminCohorts.useGetCohortTemplates();
  const cohortClients = clientData?.clients || [];
  const cohortTemplates = templateData?.templates || [];

  const clientsDropdownItems: InputDropdownItem[] = cohortClients.map((client) => ({
    text: client.name,
    textValue: client.id,
  }));

  const templateDropdownItems: InputDropdownItem[] = cohortTemplates.map((template) => ({
    text: template.cohortType,
    textValue: template.id,
  }));

  return (
    <div className='flex flex-col gap-4 min-w-[800px] pb-[118px]'>
      <InlineFormInput
        register={() => register('name', { required: 'Name is required' })}
        label='Name'
        errors={formState.errors.name}
        customWidth='w-full'
      />
      <FormsCheckBox size='sm' control={control} name='available' description='Available' />
      <InlineFormInput
        register={() =>
          register('description', {
            required: 'Description is required',
            maxLength: { value: 95, message: 'Description cannot exceed 95 characters' },
          })
        }
        label='Description'
        errors={formState.errors.description}
        customWidth='w-full'
      />
      <FormsInputDropdown
        type='default'
        control={control}
        name='clientId'
        label='Client'
        items={clientsDropdownItems}
        rules={{ required: 'Client selection is required' }}
        error={formState.errors.clientId}
        inputDisabled
      />
      <FormsInputDropdown
        type='default'
        control={control}
        name='templateId'
        label='Template'
        items={templateDropdownItems}
        rules={{ required: 'Template selection is required' }}
        error={formState.errors.templateId}
        inputDisabled
      />
      <InlineFormInput
        register={() => register('duration', { required: 'Duration is required' })}
        label='Duration'
        errors={formState.errors.duration}
        customWidth='w-full'
      />
      <InlineFormInput
        register={() => register('timeCommitment', { required: 'Time commitment is required' })}
        label='Time Commitment'
        errors={formState.errors.timeCommitment}
        customWidth='w-full'
      />
      <InlineFormInput
        prefixContent='https://'
        register={() => register('url')}
        label='Url'
        errors={formState.errors.url}
        customWidth='w-full'
      />
      <CohortDates control={control} formState={formState} />
      <ApplicationPeriod control={control} formState={formState} />
    </div>
  );
};

const CohortDates = (props: {
  control: Control<CohortFormData>;
  formState: FormState<CohortFormData>;
}) => {
  const { control, formState } = props;
  return (
    <div>
      <p className='mb-2 text-sm text-gray-500 font-medium'>Cohort dates</p>
      <div className='md:flex gap-10'>
        <Controller
          name='kickoffDate'
          control={control}
          render={({ field }) => (
            <DatePickerWithTimezone
              label='Kickoff'
              date={field.value ? new Date(field.value) : null}
              setDate={(newDate) => field.onChange(newDate)}
              errorText={formState.errors.kickoffDate?.message}
              required
              includeTime
            />
          )}
          rules={{ required: 'Kickoff date is required' }}
        />
        <Controller
          name='engagementStartDate'
          control={control}
          render={({ field }) => (
            <DatePickerWithTimezone
              label='Engagement Start'
              date={field.value ? new Date(field.value) : null}
              setDate={(newDate) => field.onChange(newDate)}
              errorText={formState.errors.engagementStartDate?.message}
              required
              includeTime
            />
          )}
          rules={{ required: 'Engagement start date is required' }}
        />
        <Controller
          name='engagementEndDate'
          control={control}
          render={({ field }) => (
            <DatePickerWithTimezone
              label='Engagement End'
              date={field.value ? new Date(field.value) : null}
              setDate={(newDate) => field.onChange(newDate)}
              errorText={formState.errors.engagementEndDate?.message}
              required
              includeTime
            />
          )}
          rules={{ required: 'Engagement end date is required' }}
        />
        <Controller
          name='wrapupDate'
          control={control}
          render={({ field }) => (
            <DatePickerWithTimezone
              label='Wrap Up'
              date={field.value ? new Date(field.value) : null}
              setDate={(newDate) => field.onChange(newDate)}
              errorText={formState.errors.wrapupDate?.message}
              required
              includeTime
            />
          )}
          rules={{ required: 'Wrapup date is required' }}
        />
      </div>
    </div>
  );
};

const ApplicationPeriod = (props: {
  control: Control<CohortFormData>;
  formState: FormState<CohortFormData>;
}) => {
  const { control, formState } = props;
  return (
    <div>
      <p className='mb-2 text-sm text-gray-500 font-medium'>Application Period</p>
      <div className='md:flex gap-10'>
        <Controller
          name='applicationStartDate'
          control={control}
          render={({ field }) => (
            <DatePickerWithTimezone
              label='Start'
              date={field.value ? new Date(field.value) : null}
              setDate={(newDate) => field.onChange(newDate)}
              errorText={formState.errors.applicationStartDate?.message}
              required
              includeTime
            />
          )}
          rules={{ required: 'Application start date is required' }}
        />
        <Controller
          name='applicationEndDate'
          control={control}
          render={({ field }) => (
            <DatePickerWithTimezone
              label='End'
              date={field.value ? new Date(field.value) : null}
              setDate={(newDate) => field.onChange(newDate)}
              errorText={formState.errors.applicationEndDate?.message}
              required
              includeTime
            />
          )}
          rules={{ required: 'Application end date is required' }}
        />
      </div>
    </div>
  );
};
